import { z } from "zod";

/**
 * Validation for the sign-in, reset and forced-change forms.
 *
 * Messages sit here rather than in the components so they read as one set, and
 * so the rules are testable without rendering. Cognito's own failures are not
 * modelled: they arrive from the auth machine and are shown form-level.
 */

/**
 * A Cognito username isn't necessarily an email — users can be created with a
 * distinct username via addUser — so it's only ever checked for presence.
 */
const username = z.string().trim().min(1, "Enter your email or username.");

/**
 * Amplify reads a blank password as "sign in without one" and switches the flow
 * to CUSTOM_AUTH, which the pool rejects with an opaque error.
 */
export const signInSchema = z.object({
  username,
  password: z.string().min(1, "Enter your password."),
});

export const resetRequestSchema = z.object({
  username,
});

/** Shared by the two screens that set a new password. */
const password = z.string().min(1, "Enter a new password.");
const confirmPassword = z.string().min(1, "Confirm your password.");
const passwordsMatch = {
  message: "Passwords do not match.",
  path: ["confirmPassword"],
};

export const forceNewPasswordSchema = z
  .object({ password, confirmPassword })
  .refine(
    (values) => values.password === values.confirmPassword,
    passwordsMatch,
  );

export const resetConfirmSchema = z
  .object({
    code: z.string().trim().min(1, "Enter the code we emailed you."),
    password,
    confirmPassword,
  })
  .refine(
    (values) => values.password === values.confirmPassword,
    passwordsMatch,
  );

export type SignInValues = z.infer<typeof signInSchema>;
export type ResetRequestValues = z.infer<typeof resetRequestSchema>;
export type ResetConfirmValues = z.infer<typeof resetConfirmSchema>;
export type ForceNewPasswordValues = z.infer<typeof forceNewPasswordSchema>;
